// Pixel e-commerce Wix intégré au bundle.
//
// Wix diffuse ses événements e-commerce (Purchase, InitiateCheckout, …) à
// tous les listeners enregistrés sur `window.wixDevelopersAnalytics` — c'est
// le mécanisme qu'utilisent les pixels FB/GA officiels. Comme eteka.js est
// déjà chargé sur toutes les pages du site (Custom Code), on s'y branche et
// on relaie vers /event avec le userId anonyme du quiz (localStorage).
//
// Le backend dédoublonne par orderId : ce pixel peut donc cohabiter avec le
// webhook Velo serveur (docs/WIX-TRACKING.md) sans double comptage.

const USER_KEY = 'eteka.userId';

// Événements Wix → types internes de la table events.
// Purchase UNIQUEMENT : checkout_started et order_placed sont envoyés par le
// webhook Velo (backend/events.js côté Wix) qui n'a pas d'orderId commun avec
// le pixel à ce stade — les tracker ici les compterait en double. L'achat,
// lui, porte un orderId des deux côtés : le backend fusionne les deux signaux.
const EVENT_MAP: Record<string, string> = {
  Purchase: 'purchase',
};

type WixEventData = Record<string, unknown> | undefined;

let apiUrl = '';
let registered = false;

/** Fixe (ou met à jour) l'URL backend utilisée par le pixel. */
export function setTrackingApiUrl(url: string | undefined): void {
  if (url) apiUrl = url.replace(/\/$/, '');
}

/** userId du quiz s'il existe — on n'en crée PAS pour une simple acheteuse. */
function quizUserId(): string | null {
  try {
    return localStorage.getItem(USER_KEY);
  } catch {
    return null;
  }
}

function send(type: string, wixEvent: string, data: WixEventData): void {
  if (!apiUrl) return;
  const d = data ?? {};
  const orderId = d['orderId'] ?? d['id'] ?? null;
  // On ne garde que des champs non-PII : ids produits, montant, devise.
  const contents = Array.isArray(d['contents'])
    ? (d['contents'] as Record<string, unknown>[]).map((c) => ({
        id: c['id'] ?? c['sku'] ?? null,
        quantity: c['quantity'] ?? null,
      }))
    : undefined;
  try {
    void fetch(`${apiUrl}/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: quizUserId(),
        type,
        orderId: orderId != null ? String(orderId) : null,
        revenue: d['revenue'] ?? d['price'] ?? null,
        currency: d['currency'] ?? null,
        meta: { source: 'wix_pixel', wixEvent, contents },
      }),
      keepalive: true,
    });
  } catch {
    /* silencieux — le tracking ne doit jamais casser le site */
  }
}

function onWixEvent(eventName: string, data?: WixEventData): void {
  const type = EVENT_MAP[eventName];
  if (type) send(type, eventName, data);
}

function tryRegister(): boolean {
  const w = window as unknown as {
    wixDevelopersAnalytics?: {
      register?: (appId: string, cb: (name: string, data?: WixEventData) => void) => void;
    };
  };
  const wda = w.wixDevelopersAnalytics;
  if (!wda?.register) return false;
  try {
    wda.register('eteka-bot', onWixEvent);
    return true;
  } catch {
    return false;
  }
}

/**
 * Installe le pixel (idempotent). Sans effet hors Wix : si
 * `wixDevelopersAnalytics` n'apparaît jamais, rien n'est envoyé.
 */
export function installWixTracking(url?: string): void {
  setTrackingApiUrl(url);
  if (registered || typeof window === 'undefined') return;
  registered = true;
  if (!tryRegister()) {
    window.addEventListener('wixDevelopersAnalyticsReady', () => {
      tryRegister();
    });
  }
}
